import React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { translate  } from 'react-i18next';
import {
  Container,
  Row,
  Col,
  Button,
  Input,
  Label,
  FormGroup,
  Collapse,
  CardBody, Card, ModalHeader, ModalBody, Modal, ModalFooter, Alert,
  FormText, FormFeedback, InputGroup,
} from 'reactstrap';
import FlightRequestRequest from '../../Store/FlightRequest/FlightRequestRequest';
import GoogleMap from '../../Components/GoogleMap';
import Location from '../../Store/Location';
import Flight from '../../Store/Flight';
import FlightRoutePoint from '../../Store/Flight/FlightRoutePoint';
import Marker from 'react-google-maps/lib/components/Marker';
import Polyline from 'react-google-maps/lib/components/Polyline';
import OverlayView from 'react-google-maps/lib/components/OverlayView';
import TextField from '../../Components/Form/TextField';
import Aircraft from '../../Store/Aircraft';
import AircraftSelect from './AircraftSelect';
import DateTimePicker from 'react-widgets/lib/DateTimePicker';
import moment from 'moment-timezone';
import ProcessingModal from '../../Booking/ProcessingModal';
import Settings from '../../Store/Settings';

const getRoutePointIcon = () => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  fillColor: 'blue',
  fillOpacity: 1,
  scale: 10,
  strokeColor: 'white',
  strokeWeight: 2,
});

@observer
export class FlightRequestViewScreen extends React.Component {

  @observable request: FlightRequestRequest = new FlightRequestRequest();

  state: {
    locations: Array<Location>,
    aircrafts: Array<Aircraft>,
  };

  constructor(props: any) {
    super(props);

    this.state = {
      locations: null,
      aircrafts: null,
    };
  }

  componentDidMount() {
    const { settings } = Settings;

    if (!settings.has('enable_flight_requests') || settings.get('enable_flight_requests') <= 0) {
      this.props.router.replace('/');
    } else {
      this.loadLocations();
      this.loadAircrafts();
    }
  }

  async loadAircrafts() {
    //store all available locations
    const aircrafts = await Aircraft.list();
    this.setState({
      aircrafts,
    });
  }

  async loadLocations() {
    //store all available locations
    const locations = await Location.listAll();
    this.setState({
      locations,
    });
  }

  getDistance = (pos1, pos2) => {

    // accept both latLng object and literal
    const lat1 = typeof pos1.lat === 'function' ? pos1.lat() : pos1.lat;
    const lat2 = typeof pos2.lat === 'function' ? pos2.lat() : pos2.lat;
    const lng1 = typeof pos1.lng === 'function' ? pos1.lng() : pos1.lng;
    const lng2 = typeof pos2.lng === 'function' ? pos2.lng() : pos2.lng;

    return Math.sqrt(
      Math.pow(Math.abs(lat1 - lat2), 2) + Math.pow(Math.abs(lng1 - lng2), 2),
    );
  };

  addPointToRoute = (data) => {
    const point = new FlightRoutePoint(data);
    this.request.route.points.push(point);

    return point;
  };

  geocodePoint = (point) => {
    this.geocoder && this.geocoder.geocode({
      location: point.latLng,
    }, (response, status) => {
      if (status === window.google.maps.GeocoderStatus.OK) {
        // if we have a city information in one of the resposnes, use it directly
        const cityNameResponse = response.find(c => {
          return c.types.indexOf('locality') !== -1;
        });

        if (cityNameResponse) {
          point.name = cityNameResponse.formatted_address;
        } else if (response.length > 0) {
          // find locality address component in any of the responses
          for (let i = 0; i < response.length; i++) {
            const cityComponent = response[i].address_components.find(c => {
              return c.types.indexOf('locality') !== -1;
            });

            if (cityComponent) {
              point.name = cityComponent.long_name;
              break;
            }
          }
        }
      }
    });
  };

  handleMapClick = ({ latLng }) => {
    const point = this.addPointToRoute({ gps_lat: latLng.lat(), gps_long: latLng.lng() });

    this.geocodePoint(point);
  };

  createHandleUpdatePoint = (point) => ({ latLng }) => {
    const zoom = this.map.getZoom();
    var scale = Math.pow(2, zoom);
    const snapDistance = 20 / scale;

    const closeLocation = this.state.locations.find(l => this.getDistance(l.latLng, latLng) < snapDistance);

    if (closeLocation) {
      point.fromLocation(closeLocation);
    } else {
      point.fromLatLng(latLng);
    }

    this.geocodePoint(point);
  };

  createHandleDeletePoint = (point) => () => {
    this.request.route.points = this.request.route.points.filter(p => p !== point);
  };

  createHandleLocationClick = (location) => () => {
    const point = new FlightRoutePoint();
    point.fromLocation(location);

    this.addPointToRoute(point);
  };

  handleMapLoad = (map) => {
    this.map = map;

    this.geocoder = new window.google.maps.Geocoder();

    if (map && this.state.locations && !this.state.mapInitialized) {
      const bounds = this.calculateBounds(this.state.locations);
      if (bounds) map.fitBounds(bounds);

      this.setState({
        mapInitialized: true,
      });
    }
  };

  handleClearRoute = () => {
    this.request.route.points = [];
  };

  handleToggle = (property) => ({ target: { checked } }) => {
    this.request.roundtrip = checked;
  };

  handleInputChange = (e) => {
    this.request[e.target.name] = e.target.value;
  };

  handleSwitchChange = (e) => {
    this.request[e.target.name] = e.target.value === 1 || e.target.value === '1';
  };

  handleDateChanged = (name) => (date) => {
    this.request[name] = moment(date);
  };

  handleAircraftChange = (aircraftModel) => {
    this.request.aircraft_model = aircraftModel;
  };

  handleSubmit = () => {
    this.setState({
      modalOpen: true,
    });

    this.request.send()
      .then(this.handleBookingSuccess)
      .catch(this.handleBookingError)
    ;
  };

  handleModalClose = () => {
    if (this.state.savedRequest) {
      this.props.router.replace('/');
    } else {
      this.setState({
        modalOpen: false,
      });
    }
  };

  handleBookingSuccess = savedRequest => {
    this.setState({
      savedRequest,
    });
  };

  handleBookingError = () => {
    this.setState({
      savedRequest: null,
    });
  };

  calculateBounds = (locations) => {
    if (locations) {
      const bounds = new window.google.maps.LatLngBounds();
      locations.map(l => bounds.extend({
        lat: parseFloat(l.gps_lat),
        lng: parseFloat(l.gps_long),
      }));

      return bounds;
    }
  };

  render() {
    const { props: { t }, request } = this;

    const { locations, aircrafts } = this.state;

    const { settings } = Settings;

    if (!settings.has('enable_flight_requests') || settings.get('enable_flight_requests') <= 0) {
      return (
        <div>
          <Container style={{ paddingBottom: 200 }}>
            No access
          </Container>
        </div>
      );
    }

    if (locations === null || aircrafts === null) {
      return null;
    }

    return (
      <div>
        <Container>
          <h1>{t('Request a Flight')}</h1>

          <Row>
            <Col md={6} style={{ display: 'flex' }}>
              <Card style={{ flex: 1 }}>
                <CardBody>
                  <Row>
                    <Col xs={12}>
                      <h2>1. {t('Flight route')}</h2>
                      <p>
                        {t('Click into the map to add a point to your route.')}
                        <br />
                        {t('Right-click on a point to delete it; left-click & drag to change it\'s position.')}
                      </p>
                    </Col>
                  </Row>
                  <Row style={{ marginBottom: 8 }}>
                    <Col xs={12}>
                      <FormGroup check>
                        <FormGroup check>
                          <Label check className='checkbox-label'>
                            <Input
                              type='checkbox'
                              onChange={this.handleToggle('roundtrip')}
                              checked={!!request.roundtrip}
                            /> {t('Return back to the pickup location')}
                          </Label>
                        </FormGroup>

                      </FormGroup>
                    </Col>
                  </Row>


                  <Row style={{ marginTop: 10 }}>
                    <Col xs={12} sm={6}>
                      <FormGroup>
                        <Label>{t('Pick-up location')}</Label><br />
                        <Input plaintext value={request.pickUpPoint ? request.pickUpPoint.title : t('N/A')} />
                      </FormGroup>
                    </Col>

                    <Col xs={12} sm={6}>
                      <FormGroup>
                        <Label>{t('Drop-off location')}</Label><br />
                        <Input plaintext value={request.dropOffPoint ? request.dropOffPoint.title : t('N/A')} />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <GoogleMap
                        onClick={this.handleMapClick}
                        defaultZoom={8}
                        onMapLoad={this.handleMapLoad}
                      >
                        {
                          locations.map(
                            (l, i) =>
                              <Marker
                                key={`location_${l.id}`}
                                position={l.latLng}
                                // label={{
                                //   text: l.name
                                // }}
                                onClick={this.createHandleLocationClick(l)}
                              />,
                          )}
                        {
                          locations.map(
                            (l, i) =>
                              <OverlayView
                                key={`location_label_${l.id}`}
                                position={l.latLng}
                                mapPaneName={OverlayView.OVERLAY_LAYER}
                                getPixelPositionOffset={(width, height) => ({
                                  x: 0,
                                  y: -height,
                                })}
                              >
                                <div style={{
                                  background: `white`,
                                  border: `0px solid #ccc`,
                                  padding: '2px 5px',
                                  opacity: 0.9,
                                }}>
                                  {l.name}
                                </div>
                              </OverlayView>,
                          )}
                        {
                          request.route.points.map(
                            (p, i) =>
                              <Marker
                                key={`point_${i}`}
                                position={p.latLng}
                                draggable
                                label={{
                                  text: `${i + 1}`,
                                  color: 'white',
                                }}
                                onDragEnd={this.createHandleUpdatePoint(p)}
                                onRightClick={this.createHandleDeletePoint(p)}
                                icon={getRoutePointIcon()}
                              />,
                          )}
                        {
                          request.route.points.length > 1 &&
                          <Polyline
                            path={request.route.points.map(p => p.latLng)}
                            options={{
                              strokeColor: 'blue',
                            }}
                          />
                        }
                        {
                          request.route.points.length > 1 && request.roundtrip && // close the path if it's A to A flight (roundtrip)
                          <Polyline
                            path={[
                              request.route.points[request.route.points.length - 1].latLng,
                              request.route.points[0].latLng,
                            ]}
                            options={{
                              strokeColor: 'blue',
                            }}
                          />
                        }
                      </GoogleMap>
                    </Col>
                  </Row>
                  <Row className='mt-3'>
                    <Col xs={12} style={{ textAlign: 'right' }}>
                      <button className='btn btn-secondary' disabled={request.route.points.length === 0}
                              onClick={this.handleClearRoute}>{t('Clear route')}</button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col md={6} style={{ display: 'flex', flexDirection: 'column' }}>
              <Card style={{ flex: 1, marginBottom: '20px' }}>
                <CardBody>
                  <Row>
                    <Col xs={12}>
                      <h2>2. {t('Flight details')}</h2>
                      <FormGroup>
                        <Label>{t('Indicate date and preferred take-off date & time')}</Label>
                        <Input
                          type='select'
                          name='is_time_range'
                          onChange={this.handleSwitchChange}
                          value={request.is_time_range ? '1' : '0'}
                        >
                          <option value='0'>{t('exact')}</option>
                          <option value='1'>{t('time range')}</option>
                        </Input>
                      </FormGroup>
                    </Col>

                    <Col xs={12} md={request.is_time_range ? 6 : 12}>
                      <FormGroup>
                        <Label>{request.is_time_range ? t('From') : t('Take-off date & time')}</Label>
                        <DateTimePicker
                          format='lll'
                          onChange={this.handleDateChanged('time_from')}
                        />
                        <Input type='hidden' invalid={!request.validDate} />
                        <FormFeedback>{request.time_from ? t('Incorrect time range') : t('This field is required')}</FormFeedback>
                      </FormGroup>
                    </Col>

                    {
                      request.is_time_range ?
                        <Col xs={12} md={request.is_time_range ? 6 : 12}>
                          <FormGroup>
                            <Label>{t('To')}</Label>
                            <DateTimePicker
                              format='lll'
                              onChange={this.handleDateChanged('time_to')}
                            />
                            <Input type='hidden' invalid={!request.validDate} />
                            <FormFeedback>{request.time_to ? t('Incorrect time range') : t('This field is required')}</FormFeedback>
                          </FormGroup>
                        </Col> : null
                    }
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <FormGroup>
                        <Label>{t('Choose your preferred aircraft type')}</Label>
                        <AircraftSelect
                          selected={this.request.aircraft_model}
                          aircrafts={aircrafts}
                          onAircraftChanged={this.handleAircraftChange}
                        />
                      </FormGroup>
                    </Col>
                    {request.aircraft_model ? (
                      <Col xs={12}>
                        <FormGroup>
                          <Label /><br />
                          <Input plaintext
                                 value={request.aircraft_model ? t('Max {{passengers_capacity}} passengers', request.aircraft_model) : ''} />
                        </FormGroup>
                      </Col>) : ''}
                  </Row>

                  <Row>
                    <Col xs={12}>
                      <FormGroup>
                        <Label>{t('Remarks: please indicate your intentions; the airline, flight number and weight & size of luggage if applicable; group size; your availability; and other relevant information')}</Label>
                        <Input type='textarea' name='note' onChange={(e) => request.note = e.target.value}
                               value={request.note} />
                      </FormGroup>
                    </Col>
                  </Row>

                </CardBody>
              </Card>
              <Card style={{ flex: 1 }}>
                <CardBody>
                  <Row>
                    <Col xs={12}>
                      <h2>3. {t('Contact details')}</h2>
                    </Col>
                    <Col xs={12} sm={6}>
                      <TextField
                        required
                        label={t('First name')}
                        name='first_name'
                        onChange={this.handleInputChange}
                        value={request.first_name}
                        placeholder={t('First name')}
                      />
                    </Col>

                    <Col xs={12} sm={6}>
                      <TextField
                        required
                        label={t('Last name')}
                        name='last_name'
                        onChange={this.handleInputChange}
                        value={request.last_name}
                        placeholder={t('Last name')}
                      />
                    </Col>

                    <Col xs={12} sm={6}>
                      <TextField
                        required
                        label={t('E-mail address')}
                        name='email'
                        onChange={this.handleInputChange}
                        value={request.email}
                        placeholder={t('E-mail address')}
                        error={!request.validEmail}
                        errorText={t('Email Adresse ist nicht gültig')}
                      />
                    </Col>

                    <Col xs={12} sm={6}>
                      <TextField
                        label={t('Phone number')}
                        name='phone'
                        onChange={this.handleInputChange}
                        value={request.phone}
                        placeholder={t('Phone number')}
                        error={request.phone && request.phone.length > 0 && !request.validPhone}
                        errorText={t('Phone number is not valid')}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className='mt-5'>
            <Col xs={12} className='text-right'>
              <Button
                color='primary'
                size='lg'
                disabled={!request.valid}
                onClick={this.handleSubmit}
              >
                {t('Send request')}
              </Button>
            </Col>
          </Row>
        </Container>

        <Modal isOpen={this.state.modalOpen}>
          <ModalHeader>
            {t('Flight request')}
          </ModalHeader>
          <ModalBody>
            {
              this.state.savedRequest === undefined ?
                <Alert color='info'>{t('Sending request...')}</Alert> :
                (
                  this.state.savedRequest ?
                    <Alert color='success'>{t('Request send. Our operator will contact you shortly.')}</Alert> :
                    <Alert color='danger'>{t('Your request could not be processed. Please try again later.')}</Alert>)
            }
          </ModalBody>
          <ModalFooter>
            <Button
              color='primary'
              size='lg'
              onClick={this.handleModalClose}
            >
              {t('Schliessen & Weiter')}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

export default translate ()(FlightRequestViewScreen);


// WEBPACK FOOTER //
// ./src/App/FlightRequest/View/index.js